import React, { createContext, useEffect } from 'react';
import type { ReactNode } from 'react';
import { lightColors, darkColors, getColorCombinations } from './colors';
import type { ThemeMode } from './types';

interface ThemeContextType {
  colors: typeof lightColors | typeof darkColors;
  colorCombinations: ReturnType<typeof getColorCombinations>;
  mode: ThemeMode;
  toggleMode: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
  defaultMode?: ThemeMode;
}

export const ThemeProvider = ({ 
  children, 
  defaultMode = 'light' 
}: ThemeProviderProps) => {
  const [mode, setMode] = React.useState<ThemeMode>(() => {
    // Check localStorage for saved theme preference
    const savedMode = localStorage.getItem('theme-mode') as ThemeMode;
    return savedMode || defaultMode;
  });

  const toggleMode = () => {
    setMode(prev => {
      const newMode = prev === 'light' ? 'dark' : 'light';
      localStorage.setItem('theme-mode', newMode);
      return newMode;
    });
  };

  // Apply theme to document root for CSS variables
  useEffect(() => {
    const root = document.documentElement;
    const currentColors = mode === 'dark' ? darkColors : lightColors;
    
    // Set CSS custom properties
    Object.entries(currentColors).forEach(([key, value]) => {
      root.style.setProperty(`--color-${key}`, value);
    });
    
    // Add/remove dark class for global styling
    if (mode === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [mode]);

  const value: ThemeContextType = {
    colors: mode === 'dark' ? darkColors : lightColors,
    colorCombinations: getColorCombinations(mode),
    mode,
    toggleMode,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
